import { inject, Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, map } from 'rxjs';
import { Role, Permission } from '../../../models/access.model';
import { BusinessModel, CreateUserRequest, UserSummary } from '../../../models/user.model';
import { UserPlanSummary } from '../../../models/subscription.model';
import { AssignUserPlanRequest, Plan } from '../../../models/plan.model';
import { Country } from '../../../models/geography.model';
import { UserRoleService } from '../../../services/user-role.service';
import { PlanService } from '../../../services/plan.service';
import { GeographyService } from '../../../services/geography.service';
import { PermissionService } from '../../../services/permission.service';
import { BusinessService } from '../../../services/business.service';
import { AuthService } from '../../../services/auth/auth.service';
import {
  BUSINESS_OWNER_ROLE,
  OWNER_ROLES,
  SYSTEM_ROLES,
  accountRoleLabel,
  AccountType,
  belongsTo,
  hasAnyRole,
  isCustomerOnly,
  isRoleOfferedOn,
  isSystemRole,
  requiresAuthenticator
} from '../../../shared/roles.constants';
import { firstValidationError } from '../../../shared/http-errors';

@Component({
  selector: 'app-user-roles-page',
  standalone: false,
  templateUrl: './user-roles-page.component.html',
  styleUrls: ['./user-roles-page.component.css']
})
export class UserRolesPageComponent {
  readonly staffAuth = inject(AuthService);
  /**
   * Which population this page shows, from route data:
   *  - 'plus'        — Plus premium accounts: AccountHolder and BusinessUser (plus staff).
   *  - 'marketplace' — marketplace sellers: MarketplaceAccountHolder, the free tier's owner role.
   *  - 'customer'    — marketplace customer accounts: MarketplaceBuyer.
   */
  userType: AccountType = 'plus';
  /** All users returned by the API, before the plus/marketplace split. */
  private allUsers: UserSummary[] = [];

  isLoading = false;
  users: UserSummary[] = [];
  allRoles: Role[] = [];
  filteredUsers: UserSummary[] = [];
  searchTerm = '';
  /**
   * Platform staff filter: everyone, the accounts that must sign in with an authenticator, or
   * only those that have not set one up yet. Combined with the search box.
   */
  staffFilter: 'all' | 'staff' | 'notSetUp' = 'all';
  selectedUser: UserSummary | null = null;
  pendingRoleName = '';
  roleSaving = false;
  roleError = '';
  deleteReason = '';
  roleRevokeReason = '';

  get canChangeSelectedUser(): boolean {
    return !!this.selectedUser && this.authService.canChangeAccount(this.selectedUser);
  }
  get canAssignSelectedUser(): boolean {
    return !!this.selectedUser && this.authService.canChangeAccount(this.selectedUser, true);
  }
  get selectedStaffRank(): number | null {
    const ranks = (this.selectedUser?.roles || []).map(r => r.staffRank).filter((r): r is number => typeof r === 'number');
    return ranks.length ? Math.max(...ranks) : null;
  }
  canManageSelectedRole(roleName: string): boolean {
    const role = this.allRoles.find(r => (r.roleName || '').toLowerCase() === (roleName || '').toLowerCase());
    return !!role && this.canChangeSelectedUser && this.authService.canManageRole(role);
  }
  confirmAddRole(): void {
    if (!this.selectedUser || !this.pendingRoleName || this.roleSaving) return;
    this.addRoleToUser(this.selectedUser, this.pendingRoleName);
  }
  cancelAddRole(): void { if (!this.roleSaving) { this.pendingRoleName = ''; this.roleError = ''; } }


  // Aggregated / legacy
  selectedUserPermissions: Permission[] = [];
  /** Set when the permission lookup fails, so empty panels aren't read as "none assigned". */
  permissionsError = '';
  toast: string = '';

  // Subscriptions (every plan record for the selected user, any status)
  userPlans: UserPlanSummary[] = [];
  plansLoading = false;
  plansError = '';

  // Admin plan override: upgrade / downgrade / disable.
  // All of these are local-only; nothing is changed at the payment provider.
  get canManagePlans(): boolean { return this.authService.can('Users.Plans.Manage'); }
  showPlanForm = false;
  planSaving = false;
  planActionError = '';
  planActionMessage = '';
  confirmCancelPlan = false;
  /** UserPlanId awaiting confirmation when cancelling one specific row. */
  confirmCancelPlanId: string | null = null;
  plans: Plan[] = [];
  countries: Country[] = [];
  planForm: FormGroup;
  private planLookupsLoaded = false;

  // System-level
  selectedUserSystemRoles: Role[] = [];
  selectedUserSystemPermissions: Permission[] = [];

  // Business-level
  businesses: BusinessModel[] = [];
  selectedUserBusinessRoles: Role[] = [];
  selectedUserBusinessPermissions: Permission[] = [];
  selectedBusiness: BusinessModel | null = null;
  selectedBusinessRoles: Role[] = [];
  selectedBusinessPermissions: Permission[] = [];
  newBusinessRoleName: string = '';
  confirmRemoveRoleName: string | null = null;
  /** System role awaiting removal confirmation. */
  confirmRemoveSystemRoleName: string | null = null;
  /**
   * Edit mode. The page opens view-only; the edit bar takes the selected user's edit lease and
   * reports here. Every control that changes the user sits in an edit-scope fieldset bound to it.
   */
  editing = false;

  // Assigning user to a business
  newBusinessId: string = '';
  /** The business role they get there; BusinessOwner makes them its owner. */
  assignRoleName: string = BUSINESS_OWNER_ROLE;
  assigningBusiness = false;
  assignBusinessError = '';
  allBusinesses: BusinessModel[] = [];

  // properties in your component
  selectedUserBusinesses: BusinessModel[] = [];

  // Deleting a user
  confirmDeleteUser = false;
  deletingUser = false;
  deleteUserError = '';

  // Disabling / enabling an account (sign-in off or on)
  confirmDisableAccount = false;
  disableReason = '';
  accountStatusSaving = false;
  accountStatusError = '';
  /** A disable that worked but left something to finish (saved sign-ins not all cleared). */
  accountStatusWarning = '';

  // Creating a user (admin)
  get canCreateUser(): boolean { return this.authService.can('Users.Create'); }
  showAddUser = false;
  addUserSaving = false;
  addUserError = '';
  addUserForm: FormGroup;
  /** Shown once after creation so the admin can hand it to the new user. */
  createdTempPassword: string | null = null;
  createdUserEmail = '';

  constructor(
    private userRoleService: UserRoleService,
    private permissionService: PermissionService,
    private businessService: BusinessService,
    private planService: PlanService,
    private geographyService: GeographyService,
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.addUserForm = this.formBuilder.group({
      firstName: this.formBuilder.control<string>('', { nonNullable: true, validators: [Validators.required] }),
      lastName: this.formBuilder.control<string>('', { nonNullable: true, validators: [Validators.required] }),
      email: this.formBuilder.control<string>('', { nonNullable: true, validators: [Validators.required, Validators.email] }),
      phoneNumber: this.formBuilder.control<string>('', { nonNullable: true }),
      roleName: this.formBuilder.control<string | null>(null)
    });

    this.planForm = this.formBuilder.group({
      planId: this.formBuilder.control<number | null>(null, [Validators.required]),
      // Required: BusinessOwner is scoped to a business, so the plan needs a target.
      businessId: this.formBuilder.control<string | null>(null, [Validators.required]),
      billingCycle: this.formBuilder.control<string>('Monthly', { nonNullable: true }),
      countryCode: this.formBuilder.control<string | null>(null),
      mode: this.formBuilder.control<'active' | 'trial'>('active', { nonNullable: true }),
      trialEndsAt: this.formBuilder.control<string>('', { nonNullable: true }),
      conversionMode: this.formBuilder.control<'auto' | 'manual'>('manual', { nonNullable: true })
    });
  }

  ngOnInit() {
    // The same component serves /users (plus) and /marketplace-users; re-filter when
    // navigating between them (Angular reuses the instance).
    this.route.data.subscribe(data => {
      const routeType = data?.['userType'];
      const nextType: AccountType =
        routeType === 'marketplace' || routeType === 'customer' ? routeType : 'plus';
      const typeChanged = nextType !== this.userType;
      this.userType = nextType;
      if (this.allUsers.length && typeChanged) {
        this.selectedUser = null;
        this.applyUserTypeFilter();
        this.onSearch(this.searchTerm);
      }
    });
    this.loadData();
    this.loadAllBusinesses();
  }

  /** Every business, with its owner, for the assign picker. */
  private loadAllBusinesses() {
    this.businessService.getBusinesses().subscribe((biz) => {
      this.allBusinesses = biz || [];
    });
  }

  get pageTitle(): string {
    if (this.userType === 'marketplace') return 'Marketplace Users';
    if (this.userType === 'customer') return 'Marketplace Customers';
    return 'Plus Users';
  }

  /** One line under the title saying who is in this list. */
  get pageSubtitle(): string {
    if (this.userType === 'marketplace') return 'Sellers on the free marketplace tier (MarketplaceAccountHolder).';
    if (this.userType === 'customer') return 'Customer accounts that buy on the marketplace (MarketplaceBuyer).';
    return 'Plus premium accounts and their business staff (AccountHolder, BusinessUser).';
  }

  /** Base route for user links, so selection URLs stay on the current page. */
  get baseRoute(): string {
    if (this.userType === 'marketplace') return '/marketplace-users';
    if (this.userType === 'customer') return '/marketplace-customers';
    return '/users';
  }

  private roleNames(user: UserSummary): string[] {
    return (user.roles || []).map(r => r.roleName).filter(name => !!name);
  }

  /** System vs business level, as dbo.Roles records it. */
  private isSystemLevel(role: { roleName: string; isSystemLevel?: boolean }): boolean {
    return isSystemRole(role);
  }

  /**
   * Split the population three ways — Plus premium, marketplace sellers, marketplace
   * customers — by the account's system roles. An account holding roles from more than
   * one population appears on each page it qualifies for.
   */
  private applyUserTypeFilter() {
    this.users = this.allUsers.filter(u => belongsTo(this.roleNames(u), this.userType));
    this.filteredUsers = [...this.users];
  }

  /** Short label for the list row, so the kind of account is visible at a glance. */
  accountLabel(user: UserSummary): string {
    return accountRoleLabel(this.roleNames(user));
  }

  // ── Authenticator (platform staff) ────────────────────────────────────

  /**
   * Must this account sign in with an authenticator app? The API says so from its own role list;
   * an older API that does not yet send the flag gets the same answer from the roles it returns.
   */
  userRequiresAuthenticator(user: UserSummary): boolean {
    return user.requiresAuthenticator ?? requiresAuthenticator(this.roleNames(user));
  }

  /** Set up and in use, as Identity records it. Only meaningful for accounts that need one. */
  authenticatorOn(user: UserSummary): boolean {
    return !!user.twoFactorEnabled;
  }

  /** Staff on this page, before the search box narrows the list. */
  get staffCount(): number {
    return this.users.filter(u => this.userRequiresAuthenticator(u)).length;
  }

  /** Staff who will be asked to set up an authenticator at their next sign-in. */
  get staffNotSetUpCount(): number {
    return this.users.filter(u => this.userRequiresAuthenticator(u) && !this.authenticatorOn(u)).length;
  }

  setStaffFilter(filter: 'all' | 'staff' | 'notSetUp'): void {
    this.staffFilter = filter;
    this.applyListFilters();
  }

  private matchesStaffFilter(user: UserSummary): boolean {
    if (this.staffFilter === 'all') return true;
    if (!this.userRequiresAuthenticator(user)) return false;
    return this.staffFilter === 'staff' || !this.authenticatorOn(user);
  }

  /**
   * A pure customer: no Plus or seller role. They own no business and hold no plan,
   * so the plan and business panels are noise rather than a problem to fix.
   */
  get selectedUserIsCustomerOnly(): boolean {
    return !!this.selectedUser && isCustomerOnly(this.roleNames(this.selectedUser));
  }

  /** Plans belong to owners; a customer account has nothing to subscribe with. */
  get canManagePlansForSelectedUser(): boolean {
    return this.canManagePlans && this.canChangeSelectedUser && this.userType !== 'customer' && !this.selectedUserIsCustomerOnly;
  }

  /** The owner role a plan grants here: sellers keep MarketplaceAccountHolder. */
  get planOwnerRole(): string {
    return this.userType === 'marketplace'
      ? SYSTEM_ROLES.MarketplaceAccountHolder
      : SYSTEM_ROLES.AccountHolder;
  }

  private loadData() {
    this.isLoading = true;
    this.userRoleService.getUsers().subscribe(users => {
      this.allUsers = users ?? [];
      this.applyUserTypeFilter();
    }).add(() => {
      this.userRoleService.getRoles().subscribe(roles => {
        this.allRoles = roles ?? [];
      }).add(() => {
        this.isLoading = false;
        // After data loads, check for a userId param and select that user if present
        const paramUserId = this.route.snapshot.paramMap.get('userId');
        if (paramUserId) {
          const match = this.users.find(u => u.userId === paramUserId);
          if (match) {
            this.onSelectUser(match);
          }
        }
      });
    });
  }

  onSearch(term: string) {
    this.searchTerm = term;
    this.applyListFilters();
  }

  /** The search box and the staff filter together, over this page's population. */
  private applyListFilters(): void {
    // Normalize the input: trim and lowercase for case-insensitive search
    const q = (this.searchTerm || '').trim().toLowerCase();
    const candidates = this.users.filter(u => this.matchesStaffFilter(u));

    if (!q) {
      this.filteredUsers = candidates;
      return;
    }

    this.filteredUsers = candidates.filter(u => {
      const name = ((u.firstName || '') + ' ' + (u.lastName || '')).toLowerCase();
      const email = (u.email || '').toLowerCase();
      // Match anywhere in name or email
      const matchBasic = name.includes(q) || email.includes(q);
      // Support searching by the local part before @ or domain part specifically
      const atIdx = email.indexOf('@');
      const local = atIdx >= 0 ? email.slice(0, atIdx) : email;
      const domain = atIdx >= 0 ? email.slice(atIdx + 1) : '';
      const matchEmailParts = (!!local && local.includes(q)) || (!!domain && domain.includes(q));
      return matchBasic || matchEmailParts;
    });
  }

  onAddRoleChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const roleName = select.value;
    select.value = '';
    if (!this.canManageSelectedRole(roleName) || this.roleSaving) return;
    this.pendingRoleName = roleName;
    this.roleError = '';
  }

  addRoleToUser(user: UserSummary, roleName: string): void {
    if (this.roleSaving || user.userId !== this.selectedUser?.userId || !this.canManageSelectedRole(roleName)) return;
    const role = this.allRoles.find(r => (r.roleName || '').toLowerCase() === (roleName || '').toLowerCase());
    if (!role) return;
    this.roleSaving = true; this.roleError = '';
    this.userRoleService.addRoleToUser(user.userId, role.roleName).subscribe({
      next: () => {
        if (!user.roles.some(r => r.roleId === role.roleId)) user.roles = [...user.roles, { ...role }];
        this.pendingRoleName = '';
        if (user.userId === this.selectedUser?.userId) this.refreshSelectedUserPermissions();
        this.toast = `Added role: ${role.displayName || role.roleName}`;
        void this.authService.loadStaffAccess(true);
      },
      error: () => this.roleError = 'Could not grant the role. Please retry.'
    }).add(() => this.roleSaving = false);
  }

  removeSystemRole(roleName: string) {
    if (!this.selectedUser || !this.canManageSelectedRole(roleName)) return;
    this.roleRevokeReason = '';
    this.confirmRemoveSystemRoleName = roleName;
  }

  cancelRemoveSystemRole() {
    if (this.roleSaving) return;
    this.confirmRemoveSystemRoleName = null;
  }

  confirmRemoveSystemRole() {
    if (!this.selectedUser || !this.confirmRemoveSystemRoleName || this.roleSaving || !this.roleRevokeReason.trim() || !this.canManageSelectedRole(this.confirmRemoveSystemRoleName)) return;
    this.roleSaving = true;
    const roleName = this.confirmRemoveSystemRoleName;
    const user = this.selectedUser;

    this.userRoleService.removeRoleFromUser(user.userId, roleName, this.roleRevokeReason.trim()).subscribe({
      next: () => {
        this.roleSaving = false;
        void this.authService.loadStaffAccess(true);
        user.roles = user.roles.filter(r => r.roleName !== roleName);
        this.refreshSelectedUserPermissions();
        this.loadSystemRolesPermissions();
        this.toast = `Removed role ${roleName}`;
        this.confirmRemoveSystemRoleName = null;
      },
      error: (error: HttpErrorResponse) => {
        this.roleSaving = false;
        this.toast = error?.error?.message
          || (error?.status === 403
            ? 'You do not have permission to remove that role.'
            : `Failed to remove role ${roleName}`);
        this.confirmRemoveSystemRoleName = null;
      }
    });
  }

  // For any legacy calls
  getUserPermissions(user: UserSummary): Permission[] {
    return this.selectedUserPermissions;
  }

  private refreshSelectedUserPermissions() {
    if (!this.selectedUser) {
      this.selectedUserPermissions = [];
      this.selectedUserSystemRoles = [];
      this.selectedUserBusinessRoles = [];
      this.selectedUserSystemPermissions = [];
      this.selectedUserBusinessPermissions = [];
      return;
    }

    const assignedRoles = this.selectedUser.roles
      .map(ur => this.allRoles.find(r => r.roleId === ur.roleId))
      .filter((r): r is Role => !!r);

    const systemRoles = assignedRoles.filter(r => this.isSystemLevel(r));
    const businessRoles = assignedRoles.filter(r => !this.isSystemLevel(r));

    this.selectedUserSystemRoles = systemRoles;
    this.selectedUserBusinessRoles = businessRoles;

    this.permissionsError = '';

    if (assignedRoles.length === 0) {
      this.selectedUserPermissions = [];
      this.selectedUserSystemPermissions = [];
      this.selectedUserBusinessPermissions = [];
      return;
    }

    // Fetch each role once and partition the results — the system and business
    // lists are subsets of the same roles, so re-fetching them doubled the calls.
    forkJoin(
      assignedRoles.map(role =>
        this.permissionService.getPermissionsForRole(role.roleId).pipe(
          map(permissions => ({ role, permissions: permissions ?? [] }))
        )
      )
    ).subscribe({
      next: results => {
        this.selectedUserPermissions = this.dedupePermissions(results.map(r => r.permissions));
        this.selectedUserSystemPermissions = this.dedupePermissions(
          results.filter(r => this.isSystemLevel(r.role)).map(r => r.permissions)
        );
        this.selectedUserBusinessPermissions = this.dedupePermissions(
          results.filter(r => !this.isSystemLevel(r.role)).map(r => r.permissions)
        );
      },
      error: () => {
        // Clear first: leaving the previous user's permissions on screen is worse
        // than showing none, because they read as belonging to this user.
        this.selectedUserPermissions = [];
        this.selectedUserSystemPermissions = [];
        this.selectedUserBusinessPermissions = [];
        this.permissionsError = 'Could not load permissions for this user.';
      }
    });
  }

  /** Flatten per-role permission lists, keeping the first entry for each id. */
  private dedupePermissions(lists: Permission[][]): Permission[] {
    const byId = new Map<string, Permission>();
    for (const list of lists) {
      for (const p of list) {
        if (!byId.has(p.permissionId)) {
          byId.set(p.permissionId, p);
        }
      }
    }
    return Array.from(byId.values());
  }

  // Collect issues present on the selected user's profile
  get userProfileIssues(): string[] {
    const issues: string[] = [];
    const u = this.selectedUser;
    if (!u) return issues;

    const firstNameMissing = !u.firstName || !u.firstName.trim();
    const lastNameMissing = !u.lastName || !u.lastName.trim();
    if (firstNameMissing || lastNameMissing) {
      if (firstNameMissing && lastNameMissing) {
        issues.push('Missing first and last name');
      } else if (firstNameMissing) {
        issues.push('Missing first name');
      } else if (lastNameMissing) {
        issues.push('Missing last name');
      }
    }

    // Check for roles shown under System Roles that are flagged as non-system
    const misclassifiedSystemRoles = this.selectedUserSystemRoles.filter(r => r.isSystemLevel === false);
    if (misclassifiedSystemRoles.length > 0) {
      const names = misclassifiedSystemRoles.map(r => r.displayName || r.roleName).filter(Boolean).join(', ');
      issues.push(`System roles marked as non-system: ${names}`);
    }

    // Missing any system role
    if ((this.selectedUserSystemRoles || []).length === 0) {
      issues.push('Missing required system role');
    }

    // A customer account owns no business, so an empty list is expected, not a fault.
    if (!this.selectedUserIsCustomerOnly && (this.selectedUserBusinesses || []).length === 0) {
      issues.push('No businesses assigned to this user');
    }

    return issues;
  }


private loadUserAccessSummary() {
  if (!this.selectedUser) return;

  const userId = this.selectedUser.userId;
  this.isLoading = true;

  this.userRoleService.getUserBusinessess(userId)
    .subscribe((businesses: BusinessModel[]) => {
      // A slow answer for a user the admin has already moved away from is not this user's.
      if (!this.isSelected(userId)) return;
      this.selectedUserBusinesses = businesses;
      this.selectedBusiness = null; // reset previously selected business
    })
    .add(() => {
      this.isLoading = false;
    });
}

/** Is this still the user on screen? Responses for anyone else are dropped. */
private isSelected(userId: string): boolean {
  return !!this.selectedUser && this.selectedUser.userId === userId;
}

/** The edit bar took or gave back the user's edit lease. */
onEditingChange(editing: boolean): void {
  this.editing = editing;
  if (!editing) {
    // Half-finished edits and open confirmations belong to edit mode.
    this.showPlanForm = false;
    this.confirmCancelPlan = false;
    this.confirmCancelPlanId = null;
    this.confirmRemoveSystemRoleName = null;
    this.confirmRemoveRoleName = null;
    this.confirmDeleteUser = false;
    this.confirmDisableAccount = false;
  }
}

/** Marketplace customers own no business, so the customers page does not offer assigning one. */
get canAssignBusinesses(): boolean {
  return this.userType !== 'customer';
}

// make sure onSelectUser calls this
onSelectUser(user: UserSummary) {
  if (this.roleSaving || this.deletingUser || this.planSaving) return;
  this.confirmDeleteUser = false; this.deleteUserError = ''; this.confirmRemoveRoleName = null;
  this.pendingRoleName = ''; this.roleError = ''; this.deleteReason = ''; this.roleRevokeReason = '';
  this.selectedUser = user;
  this.showAddUser = false;
  // A different user starts view-only; the edit bar gives back the previous user's lease.
  this.onEditingChange(false);
  this.selectedUserSystemRoles = [];         // whatever you already do
  this.selectedUserSystemPermissions = [];   // ...
  this.selectedUserBusinesses = [];
  this.selectedBusiness = null;
  this.confirmRemoveSystemRoleName = null;
  this.newBusinessId = '';
  this.assignRoleName = BUSINESS_OWNER_ROLE;
  this.assignBusinessError = '';
  // An assign still in flight for the previous user must not hold this one's button disabled.
  this.assigningBusiness = false;
  this.accountStatusSaving = false;
  this.accountStatusError = '';
  this.accountStatusWarning = '';

  this.loadUserAccessSummary();
  // Refresh roles and permissions now that a user is selected
  this.refreshSelectedUserPermissions();

  // Load system-level roles and permissions via service for efficiency
  this.loadSystemRolesPermissions();

  // Plan actions belong to the previously selected user; don't carry them over.
  this.showPlanForm = false;
  this.confirmCancelPlan = false;
  this.confirmCancelPlanId = null;
  this.planActionError = '';
  this.planActionMessage = '';

  this.loadUserPlans(user.userId);

  // Do not force URL navigation here; the template's routerLink handles
  // updating the URL when the user is clicked. This keeps `/users` optional.
}

/** Every plan record for the selected user (any status), newest first. */
private loadUserPlans(userId: string): void {
  this.userPlans = [];
  this.plansError = '';
  this.plansLoading = true;
  this.userRoleService.getUserPlans(userId).subscribe({
    next: plans => (this.userPlans = plans || []),
    error: () => (this.plansError = 'Failed to load subscriptions.')
  }).add(() => (this.plansLoading = false));
}

/** The plan record currently in force, if any. */
get activeUserPlan(): UserPlanSummary | null {
  return this.userPlans.find(p => p.isActive) ?? null;
}

/**
 * A user should only ever hold one active plan. Surface a breach rather than
 * silently rendering whichever row happened to come back first.
 */
get activeUserPlanCount(): number {
  return this.userPlans.filter(p => p.isActive).length;
}

startChangePlan(): void {
    if (!this.staffAuth.can('Users.Plans.Manage')) return;
    if (!this.canChangeSelectedUser) return;
  if (!this.selectedUser) return;
  this.planActionError = '';
  this.planActionMessage = '';
  this.confirmCancelPlan = false;
  this.planForm.reset({
    planId: null,
    // Preselect when there's exactly one business; otherwise force a choice.
    businessId: this.selectedUserBusinesses.length === 1 ? this.selectedUserBusinesses[0].businessId : null,
    billingCycle: this.activeUserPlan?.billingCycle || 'Monthly',
    countryCode: null,
    mode: 'active',
    trialEndsAt: '',
    conversionMode: 'manual'
  });
  this.showPlanForm = true;
  this.loadPlanLookups();
}

cancelChangePlan(): void {
  this.showPlanForm = false;
  this.planSaving = false;
  this.planActionError = '';
}

/**
 * Plans and countries for the change-plan form. Latched only on success so a
 * failed load can be retried by reopening the form.
 */
private loadPlanLookups(): void {
  if (this.planLookupsLoaded) return;

  forkJoin({
    plans: this.planService.getPlans(),
    countries: this.geographyService.getCountries()
  }).subscribe({
    next: ({ plans, countries }) => {
      this.plans = (plans || []).filter(p => p.isActive);
      this.countries = countries || [];
      this.planLookupsLoaded = true;
    },
    error: () => {
      this.planActionError = 'Could not load plans and countries. Close the form and try again.';
    }
  });
}

submitChangePlan(): void {
    if (!this.staffAuth.can('Users.Plans.Manage')) return;
    if (!this.canChangeSelectedUser) return;
    if (!window.confirm('Apply this plan override? Billing at the payment provider will not change.')) return;
  if (this.planSaving || !this.selectedUser || !this.canManagePlansForSelectedUser) return;

  this.planForm.markAllAsTouched();
  if (this.planForm.invalid) {
    if (this.planLookupsLoaded) {
      this.planActionError = 'Choose a plan and the business it is for.';
    }
    return;
  }

  const value = this.planForm.getRawValue();
  const isTrial = value.mode === 'trial';
  const request: AssignUserPlanRequest = {
    planId: Number(value.planId),
    businessId: String(value.businessId),
    // The page already knows which population it is showing, so state it rather than
    // making the API guess from existing roles. Only the two owner populations reach
    // here — customers are blocked by canManagePlansForSelectedUser.
    accountType: this.userType === 'marketplace' ? 'marketplace' : 'plus',
    billingCycle: value.billingCycle || 'Monthly',
    countryCode: value.countryCode || null,
    mode: value.mode,
    trialEndsAt: isTrial && value.trialEndsAt ? value.trialEndsAt : null,
    conversionMode: isTrial ? value.conversionMode : null
  };

  const userId = this.selectedUser.userId;
  this.planSaving = true;
  this.planActionError = '';
  this.planActionMessage = '';

  this.userRoleService.assignUserPlan(userId, request).subscribe({
    next: (response) => {
      this.showPlanForm = false;
      const planName = this.plans.find(p => p.planID === request.planId)?.planName ?? 'plan';
      const processor = response?.resolvedProcessor
        ? ` Pricing resolved via ${response.resolvedProcessor}; no subscription was created there.`
        : '';
      this.planActionMessage = response?.warning
        ? `Moved to ${planName}. ${response.warning}`
        : `Moved to ${planName}. Granted ${response?.systemRole} and BusinessOwner.${processor}`;
      this.loadUserPlans(userId);
      // The grants change what the roles/permissions panels show.
      this.refreshSelectedUserPermissions();
      this.loadSystemRolesPermissions();
    },
    error: (error: HttpErrorResponse) => {
      this.planActionError = error?.error?.message || 'Failed to change the plan.';
    }
  }).add(() => (this.planSaving = false));
}

/**
 * Resolving a duplicate: cancel one named row rather than "the active plan",
 * which is ambiguous when there is more than one.
 */
requestCancelPlanRow(plan: UserPlanSummary): void {
    if (!this.staffAuth.can('Users.Plans.Manage')) return;
    if (!this.canChangeSelectedUser) return;
  this.planActionError = '';
  this.planActionMessage = '';
  this.confirmCancelPlan = false;
  this.confirmCancelPlanId = plan.userPlanId;
}

dismissCancelPlanRow(): void {
  this.confirmCancelPlanId = null;
}

confirmCancelPlanRow(): void {
    if (!this.staffAuth.can('Users.Plans.Manage')) return;
    if (!this.canChangeSelectedUser) return;
  if (this.planSaving || !this.selectedUser || !this.confirmCancelPlanId) return;

  const userId = this.selectedUser.userId;
  const userPlanId = this.confirmCancelPlanId;
  this.planSaving = true;
  this.planActionError = '';
  this.planActionMessage = '';

  this.userRoleService.cancelUserPlanById(userId, userPlanId).subscribe({
    next: () => {
      this.confirmCancelPlanId = null;
      this.planActionMessage = 'Plan cancelled. Billing at the payment provider is unchanged.';
      this.loadUserPlans(userId);
    },
    error: (error: HttpErrorResponse) => {
      this.planActionError = error?.error?.message || 'Failed to cancel that plan.';
    }
  }).add(() => (this.planSaving = false));
}

requestCancelPlan(): void {
    if (!this.staffAuth.can('Users.Plans.Manage')) return;
    if (!this.canChangeSelectedUser) return;
  this.planActionError = '';
  this.planActionMessage = '';
  this.confirmCancelPlan = true;
}

dismissCancelPlan(): void {
  this.confirmCancelPlan = false;
}

confirmDisablePlan(): void {
    if (!this.staffAuth.can('Users.Plans.Manage')) return;
    if (!this.canChangeSelectedUser) return;
  if (this.planSaving || !this.selectedUser || !this.canManagePlans) return;

  const userId = this.selectedUser.userId;
  this.planSaving = true;
  this.planActionError = '';
  this.planActionMessage = '';

  this.userRoleService.cancelUserPlan(userId).subscribe({
    next: () => {
      this.confirmCancelPlan = false;
      this.planActionMessage = 'Subscription disabled. Billing at the payment provider is unchanged.';
      this.loadUserPlans(userId);
    },
    error: (error: HttpErrorResponse) => {
      this.planActionError = error?.error?.message || 'Failed to disable the subscription.';
    }
  }).add(() => (this.planSaving = false));
}

planStatusLabel(plan: UserPlanSummary): string {
  const status = (plan.status || '').toLowerCase();
  if (status === 'trial') return 'Trial';
  if (plan.isActive) return 'Active';
  return plan.status ? plan.status.charAt(0).toUpperCase() + plan.status.slice(1) : 'Inactive';
}

planStatusClass(plan: UserPlanSummary): string {
  const status = (plan.status || '').toLowerCase();
  if (status === 'trial') return 'trial';
  if (plan.isActive) return 'ok';
  return 'inactive';
}

// called when user clicks on a business card
onSelectBusiness(business: BusinessModel) {
  this.selectedBusiness = business;
  this.loadBusinessRolesAndPermissions(business.businessId);
}

  /** Businesses the selected user is not linked to yet, by name. */
  get assignableBusinesses(): BusinessModel[] {
    const linked = new Set(this.selectedUserBusinesses.map(b => String(b.businessId).toLowerCase()));
    return this.allBusinesses
      .filter(b => !linked.has(String(b.businessId).toLowerCase()))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  /** Owner and headcount beside the name, so a taken business is visible before picking it. */
  businessOptionLabel(b: BusinessModel): string {
    const owner = b.ownerUserId ? `owned by ${b.ownerName || 'another user'}` : 'no owner';
    const members = b.userCount ? `, ${b.userCount} ${b.userCount === 1 ? 'member' : 'members'}` : '';
    return `${b.name} (${owner}${members})`;
  }

  /**
   * The business roles dbo.Roles offers (IsSystemLevel off), owner first. Nothing is offered
   * until the roles have loaded, so the picker never names a role the platform does not have.
   */
  get assignRoleOptions(): { roleName: string; label: string }[] {
    const businessRoles = (this.allRoles || []).filter(r => !this.isSystemLevel(r) && !!r.roleName);
    const owner = businessRoles.filter(r => r.roleName === BUSINESS_OWNER_ROLE)
      .map(r => ({ roleName: r.roleName, label: 'Owner' }));
    const staff = businessRoles
      .filter(r => r.roleName !== BUSINESS_OWNER_ROLE)
      .map(r => ({ roleName: r.roleName, label: r.displayName || r.roleName }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [...owner, ...staff];
  }

  /** The chosen role is one dbo.Roles still offers as a business role. */
  get assignRoleIsOffered(): boolean {
    return this.assignRoleOptions.some(o => o.roleName === this.assignRoleName);
  }

  /**
   * Owner chosen for a user who is not an account holder. Only an account holder (AccountHolder
   * for Plus, MarketplaceAccountHolder for Marketplace) can own a business - the business is billed
   * under the owner's plan, and the plan comes with that role - so the API refuses anyone else.
   * An employee becomes the owner only by being promoted: the holder role first, then Owner.
   */
  get assignOwnerNotHolder(): boolean {
    if (this.assignRoleName !== BUSINESS_OWNER_ROLE || !this.selectedUser) return false;
    return !hasAnyRole((this.selectedUser.roles || []).map(r => r.roleName), OWNER_ROLES);
  }

  /** Who already owns the picked business, when the selected user would be made a second owner. */
  get assignOwnerConflict(): string | null {
    if (this.assignRoleName !== BUSINESS_OWNER_ROLE || !this.selectedUser) return null;
    const business = this.allBusinesses.find(b => String(b.businessId) === String(this.newBusinessId));
    if (!business?.ownerUserId) return null;
    if (business.ownerUserId.toLowerCase() === this.selectedUser.userId.toLowerCase()) return null;
    return business.ownerName || 'another user';
  }

  addUserToBusiness(businessId: string) {
    if (!this.canAssignSelectedUser) return;
    if (!this.staffAuth.can('Businesses.Membership.Manage')) return;
    businessId = (businessId || '').trim();
    if (!this.selectedUser || !businessId || !this.editing || this.assigningBusiness
      || this.assignOwnerConflict || this.assignOwnerNotHolder || !this.assignRoleIsOffered) return;
    const userId = this.selectedUser.userId;
    const roleName = this.assignRoleName;
    const businessName = this.allBusinesses.find(b => String(b.businessId) === businessId)?.name || 'the business';
    this.assigningBusiness = true;
    this.assignBusinessError = '';
    this.userRoleService.addUserToBusiness(userId, businessId, roleName).subscribe({
      next: (response) => {
        // The picker shows owners, and this may have just made one - true whoever is on screen.
        this.loadAllBusinesses();
        // The admin may have moved to another user while this was in flight; everything below
        // describes the user it was for, so it must not land on whoever is selected now.
        if (!this.isSelected(userId)) return;

        this.toast = response?.alreadyAssigned
          ? `Already assigned to ${businessName}.`
          : response?.isOwner
            ? `Now the owner of ${businessName}.`
            : `Assigned to ${businessName} as ${response?.roleName || roleName}.`;
        this.newBusinessId = '';
        this.assignRoleName = BUSINESS_OWNER_ROLE;

        // Refresh businesses and auto-select the new one
        this.userRoleService.getUserBusinessess(userId).subscribe((biz) => {
          if (!this.isSelected(userId)) return;
          this.selectedUserBusinesses = biz || [];
          const newlyAssigned = this.selectedUserBusinesses.find(b => String(b.businessId) === String(businessId));
          if (newlyAssigned) {
            this.onSelectBusiness(newlyAssigned);
            // Assigning a business is the step before a plan; fill it in if that form is open.
            if (this.showPlanForm && !this.planForm.get('businessId')?.value) {
              this.planForm.patchValue({ businessId: newlyAssigned.businessId });
            }
          }
        });
      },
      error: (error: HttpErrorResponse) => {
        if (!this.isSelected(userId)) return;
        this.assignBusinessError = this.assignErrorMessage(error, businessName);
      }
    }).add(() => {
      if (this.isSelected(userId)) {
        this.assigningBusiness = false;
      }
    });
  }

  /**
   * The API explains its own refusals in `message` - including its 404s for a missing user or
   * business. A 404 with no message never came from the assign endpoint: the server does not
   * have it, which is what an out-of-date API looks like.
   */
  private assignErrorMessage(error: HttpErrorResponse, businessName: string): string {
    const message = error?.error?.message;
    if (message) return message;
    if (error?.status === 404) {
      return 'The server could not find the assign service. The API may be older than this admin app; '
        + 'try again after it is updated.';
    }
    if (error?.status === 403) return 'You are not allowed to make this assignment.';
    return `Failed to assign to ${businessName}.`;
  }

private loadBusinessRolesAndPermissions(businessId: string) {
  if (!this.selectedUser) return;
  const userId = this.selectedUser.userId;
  this.userRoleService.getUserBusinessAccess(userId, businessId).subscribe(({ roles, userPermissions }) => {
    this.selectedBusinessRoles = roles ?? [];
    const list = (userPermissions ?? []) as any[];
    this.selectedBusinessPermissions = list.map((p: any) => ({
      permissionId: p.code,
      code: p.code,
      name: p.name,
      category: p.category,
      description: p.description,
      grantedByRoles: p.grantedByRoles ?? p.granted_by_roles ?? p.grantedRoles ?? ''
    } as Permission));
  });

}

  get availableBusinessRoleNames(): string[] {
    const assigned = new Set((this.selectedBusinessRoles || []).map(r => (r.roleName || r.displayName || '').toLowerCase()));
    return (this.allRoles || [])
      .map(r => r.roleName || r.displayName || '')
      .filter(name => !!name && !assigned.has(name.toLowerCase()))
      .sort((a, b) => a.localeCompare(b));
  }

  private loadSystemRolesPermissions() {
    if (!this.selectedUser) {
      this.selectedUserSystemRoles = [];
      this.selectedUserSystemPermissions = [];
      return;
    }
    const userId = this.selectedUser.userId;
    this.userRoleService.getSystemRolesPermissions(userId).subscribe((resp: any) => {
      // Support both shapes:
      // { roles: Role[], userPermissions: Permission[] }
      // { role: Role, permissions: Permission[] }
      const rolesVal = resp?.roles ?? (resp?.role ? [resp.role] : []);
      const permsVal = resp?.permissions ?? [];

      if (this.selectedUser?.userId !== userId) return;
      this.selectedUserSystemRoles = Array.isArray(rolesVal) ? rolesVal : [];
      this.selectedUser.roles = this.selectedUser.roles.map(role => {
        const current = this.selectedUserSystemRoles.find(r => r.roleId === role.roleId);
        return current ? { ...role, staffRank: current.staffRank } : role;
      });
      const list = (Array.isArray(permsVal) ? permsVal : []) as any[];

      this.selectedUserSystemPermissions = list.map((p: any) => ({
        permissionId: p.permissionId ?? p.code,
        code: p.code,
        name: p.name,
        category: p.category,
        description: p.description,
        grantedByRoles: p.grantedByRoles ?? ''
      } as Permission));
    });
  }

  // Prefer using full role objects so we can show displayName and use roleName as the value.
  get availableBusinessRoles(): Role[] {
    // Only consider business-level roles (isSystemLevel === false) with a defined roleName; displayName is for label only
    const assigned = new Set((this.selectedBusinessRoles || [])
      .map(r => (r.roleName || '').toLowerCase())
      .filter(n => !!n));
    return (this.allRoles || [])
      .filter(r => !this.isSystemLevel(r) && !!r.roleName && !assigned.has(r.roleName.toLowerCase()))
      .sort((a, b) => (
        (a.displayName || a.roleName || '').localeCompare(b.displayName || b.roleName || '')
      ));
  }

  onBusinessRoleSelect(event: Event) {
    if (!this.staffAuth.can('Businesses.Membership.Manage')) return;
    const target = event.target as HTMLSelectElement | null;
    const value = (target?.value || '').trim();
    this.newBusinessRoleName = value || '';
  }

  get availableRolesForSelectedUser(): Role[] {
    // Only show unassigned system-level roles in the system roles dropdown
    const systemRoles = (this.allRoles || []).filter(r => this.isSystemLevel(r) && this.canManageSelectedRole(r.roleName));
    if (!this.selectedUser) return systemRoles;
    const assignedIds = new Set(this.selectedUser.roles.map(r => r.roleId));
    return systemRoles.filter(r => !assignedIds.has(r.roleId));
  }

  addBusinessRole(roleName: string) {
    if (!this.canAssignSelectedUser) return;
    if (!this.staffAuth.can('Businesses.Membership.Manage')) return;
    roleName = (roleName || '').trim();
    if (!this.selectedUser || !this.selectedBusiness || !roleName) return;
    const userId = this.selectedUser.userId;
    const businessId = this.selectedBusiness.businessId;
    this.userRoleService
      .addRoleToUserInBusiness(userId, String(businessId), roleName)
      .subscribe({
        next: () => {
          this.toast = `Added role ${roleName}`;
          this.loadBusinessRolesAndPermissions(String(businessId));
          this.newBusinessRoleName = '';
        },
        error: () => {
          this.toast = `Failed to add role ${roleName}`;
        },
      });
  }

  cancelRemoveBusinessRole() {
    this.confirmRemoveRoleName = null;
  }

  confirmRemoveBusinessRole() {
    if (!this.canAssignSelectedUser) return;
    if (!this.staffAuth.can('Businesses.Membership.Manage')) return;
    if (!this.selectedUser || !this.selectedBusiness || !this.confirmRemoveRoleName) return;
    const roleName = this.confirmRemoveRoleName;
    const userId = this.selectedUser.userId;
    const businessId = this.selectedBusiness.businessId;
    this.userRoleService
      .removeRoleFromUserInBusiness(userId, String(businessId), roleName)
      .subscribe({
        next: () => {
          this.toast = `Removed role ${roleName} from ${this.selectedUser!.firstName}`;
          this.loadBusinessRolesAndPermissions(String(businessId));
          this.confirmRemoveRoleName = null;
        },
        error: () => {
          this.toast = `Failed to remove role ${roleName}`;
          this.confirmRemoveRoleName = null;
        },
      });
  }

  removeBusinessRole(roleName: string) {
    if (!this.staffAuth.can('Businesses.Membership.Manage')) return;
    if (!this.selectedUser || !this.selectedBusiness || !roleName) return;
    this.confirmRemoveRoleName = roleName;
  }

  // ── Create user ────────────────────────────────────────────────────────

  /**
   * System roles offered when creating a user. Roles belonging to another population are
   * left out — creating a MarketplaceBuyer from the Plus page would file the new account
   * under a page the admin isn't looking at.
   */
  get systemRoleOptions(): Role[] {
    return (this.allRoles || [])
      .filter(r => this.isSystemLevel(r) && r.staffRank === null && isRoleOfferedOn(r.roleName, this.userType))
      .sort((a, b) => (a.displayName || a.roleName || '').localeCompare(b.displayName || b.roleName || ''));
  }

  /** The role a new account on this page gets by default. */
  private get defaultNewUserRole(): string | null {
    if (this.userType === 'marketplace') return SYSTEM_ROLES.MarketplaceAccountHolder;
    if (this.userType === 'customer') return SYSTEM_ROLES.MarketplaceBuyer;
    return null;
  }

  startAddUser(): void {
    if (!this.staffAuth.can('Users.Create')) return;
    this.showAddUser = true;
    this.selectedUser = null;
    this.addUserError = '';
    this.createdTempPassword = null;
    this.createdUserEmail = '';
    this.addUserForm.reset({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      // Each page defaults to its own population's role.
      roleName: this.defaultNewUserRole
    });
  }

  cancelAddUser(): void {
    this.showAddUser = false;
    this.addUserSaving = false;
    this.addUserError = '';
  }

  dismissTempPassword(): void {
    this.createdTempPassword = null;
    this.createdUserEmail = '';
  }

  submitAddUser(): void {
    if (!this.staffAuth.can('Users.Create')) return;
    if (this.addUserSaving || !this.canCreateUser) {
      return;
    }

    this.addUserForm.markAllAsTouched();
    if (this.addUserForm.invalid) {
      return;
    }

    const value = this.addUserForm.getRawValue();
    const request: CreateUserRequest = {
      email: (value.email || '').trim(),
      firstName: (value.firstName || '').trim(),
      lastName: (value.lastName || '').trim(),
      phoneNumber: (value.phoneNumber || '').trim() || null,
      roleName: value.roleName || null
    };

    if (request.roleName && !this.systemRoleOptions.some(r => r.roleName === request.roleName)) {
      this.addUserError = 'Select an available non-staff role. Grant staff roles after creating the account.'; return;
    }
    this.addUserSaving = true;
    this.addUserError = '';

    this.userRoleService.createUser(request).subscribe({
      next: (response) => {
        this.showAddUser = false;
        this.toast = `Created user ${request.firstName} ${request.lastName}`;
        this.createdTempPassword = response?.tempPassword || null;
        this.createdUserEmail = request.email;
        this.reloadUsersAndSelect(response?.userId);
      },
      error: (error: HttpErrorResponse) => {
        this.addUserError = error?.error?.message
          || firstValidationError(error)
          || 'Failed to create user.';
      }
    }).add(() => (this.addUserSaving = false));
  }

  // ── Disable / enable account ───────────────────────────────────────────

  /**
   * May the signed-in admin switch this account's sign-in off or on? Never their own; otherwise
   * Users.Disable plus the rank rule. The API enforces the same rules - this only spares the
   * admin a button that can only be refused.
   */
  get canChangeAccountStatus(): boolean {
    const user = this.selectedUser;
    if (!user) return false;
    if (user.userId === this.authService.getUserId()) return false;
    return this.authService.can('Users.Disable') && this.authService.canChangeAccount(user);
  }

  requestDisableAccount(): void {
    if (!this.canChangeAccountStatus) return;
    this.confirmDisableAccount = true;
    this.disableReason = '';
    this.accountStatusError = '';
  }

  cancelDisableAccount(): void {
    this.confirmDisableAccount = false;
    this.accountStatusError = '';
  }

  confirmDisableSelectedAccount(): void {
    if (!this.canChangeAccountStatus) return;
    this.changeAccountStatus(true);
  }

  enableSelectedAccount(): void {
    if (!this.canChangeAccountStatus) return;
    this.changeAccountStatus(false);
  }

  private changeAccountStatus(disable: boolean): void {
    const user = this.selectedUser;
    if (!user || !this.editing || this.accountStatusSaving) return;
    const userId = user.userId;
    const reason = this.disableReason.trim() || null;

    this.accountStatusSaving = true;
    this.accountStatusError = '';
    this.accountStatusWarning = '';
    const request = disable
      ? this.userRoleService.disableUser(userId, reason)
      : this.userRoleService.enableUser(userId);

    request.subscribe({
      next: response => {
        // The list row and the selection are the same object, so both show the new state.
        user.isDisabled = response?.isDisabled ?? disable;
        user.disabledAtUtc = user.isDisabled ? (user.disabledAtUtc || new Date().toISOString()) : null;
        user.disabledReason = user.isDisabled ? (user.disabledReason || reason) : null;
        if (!this.isSelected(userId)) return;
        this.confirmDisableAccount = false;
        this.accountStatusWarning = response?.warning || '';
        this.toast = response?.message || (disable ? 'Account disabled.' : 'Account enabled.');
      },
      error: (error: HttpErrorResponse) => {
        if (!this.isSelected(userId)) return;
        this.accountStatusError = error?.error?.message
          || (disable ? 'Failed to disable the account.' : 'Failed to enable the account.');
      }
    }).add(() => {
      if (this.isSelected(userId)) this.accountStatusSaving = false;
    });
  }

  private reloadUsersAndSelect(userId: string | undefined): void {
    this.isLoading = true;
    this.userRoleService.getUsers().subscribe(users => {
      this.allUsers = users ?? [];
      this.applyUserTypeFilter();
      this.onSearch(this.searchTerm);
      if (userId) {
        const created = this.users.find(u => u.userId === userId);
        if (created) {
          this.onSelectUser(created);
        }
      }
    }).add(() => this.isLoading = false);
  }

  // ── Delete user ────────────────────────────────────────────────────────

  requestDeleteUser() {
    if (!this.staffAuth.can('Users.Delete')) return;
    if (!this.selectedUser) return;
    if (!this.canChangeSelectedUser) return;
    this.deleteReason = '';
    this.confirmDeleteUser = true;
    this.deleteUserError = '';
  }

  cancelDeleteUser() {
    this.confirmDeleteUser = false;
    this.deleteUserError = '';
  }

  confirmDeleteSelectedUser() {
    if (!this.staffAuth.can('Users.Delete')) return;
    if (!this.selectedUser || this.deletingUser || !this.canChangeSelectedUser || !this.deleteReason.trim()) return;
    const user = this.selectedUser;

    this.deletingUser = true;
    this.deleteUserError = '';

    this.userRoleService.deleteUser(user.userId, this.deleteReason.trim()).subscribe({
      next: () => {
        this.confirmDeleteUser = false;
        this.toast = `Deleted user ${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Deleted user';
        // Remove from the list and clear the selection.
        this.users = this.users.filter(u => u.userId !== user.userId);
        this.filteredUsers = this.filteredUsers.filter(u => u.userId !== user.userId);
        this.selectedUser = null;
        this.selectedUserBusinesses = [];
        this.selectedBusiness = null;
        this.refreshSelectedUserPermissions();
      },
      error: (err) => {
        // 409 = user has business/subscription/payment history; surface the server's message.
        this.deleteUserError = err?.error?.message || 'Failed to delete user.';
      }
    }).add(() => this.deletingUser = false);
  }
}
